// src/utils/freedomNumberCalc.ts
// ─────────────────────────────────────────────────────────────────────────────
// Shared Freedom Number calculation — used by FreedomNumberReveal and App.tsx
// Brief 46B Section 1c: state pension offset integrated per-phase.
// ─────────────────────────────────────────────────────────────────────────────

import { ClientData } from '../types/ClientData';
import { PLSA } from '../config/plsaConfig';
import { STATE_PENSION } from '../taxConfig';

export function calcFreedomNumber(data: ClientData) {
  const isCouple = data.isCouple;
  const freedomAge = data.targetFreedomAge;
  const yearsToFreedom = Math.max(0, freedomAge - data.clientAge);

  // Phased spending
  const p1Annual = isCouple ? PLSA.couple.comfortable : PLSA.single.comfortable;
  const p2Annual = isCouple ? PLSA.couple.moderate : PLSA.single.moderate;
  const p3Annual = isCouple ? PLSA.couple.minimum : PLSA.single.minimum;

  const p1Years = Math.max(0, 75 - freedomAge);
  const p2Years = 10; // 75–85
  const p3Years = 15; // 85–100

  const totalYears = p1Years + p2Years + p3Years;
  const weightedAnnual = totalYears > 0
    ? (p1Annual * p1Years + p2Annual * p2Years + p3Annual * p3Years) / totalYears
    : p1Annual;

  // Years mortgage extends past freedom age (if any)
  const mortgageYearsPastFreedom = (data.mortgageTermYears || 0) > yearsToFreedom
    ? Math.max(0, (data.mortgageTermYears || 0) - yearsToFreedom)
    : 0;

  // Present value of mortgage payments for those years only (at 4% discount rate)
  const annualMortgage = (data.mortgagePayment || 0) * 12;
  const mortgagePV = mortgageYearsPastFreedom > 0 && annualMortgage > 0
    ? annualMortgage * ((1 - Math.pow(1.04, -mortgageYearsPastFreedom)) / 0.04)
    : 0;

  // ── State pension offset — per-phase calculation ─────────────────────────
  const spAge = data.statePensionAge ?? STATE_PENSION.qualifyingAge;
  const spAnnual = data.statePensionAnnual || STATE_PENSION.annualAmount;

  const phase1End = 75;
  const phase2End = 85;
  const phase3End = 100;

  // For each phase, calculate blended net annual spending (full spend pre-SP, reduced post-SP)
  const blendedNetForPhase = (phaseStart: number, phaseEnd: number, phaseAnnual: number): number => {
    const phaseYears = Math.max(0, phaseEnd - phaseStart);
    if (phaseYears <= 0) return phaseAnnual;
    const preSP = Math.max(0, Math.min(phaseYears, spAge - phaseStart));
    const postSP = Math.max(0, phaseYears - preSP);
    return (preSP * phaseAnnual + postSP * Math.max(0, phaseAnnual - spAnnual)) / phaseYears;
  };

  const p1Net = blendedNetForPhase(freedomAge, phase1End, p1Annual);
  const p2Net = blendedNetForPhase(phase1End, phase2End, p2Annual);
  const p3Net = blendedNetForPhase(phase2End, phase3End, p3Annual);

  // Blended net annual (after state pension) for pot sizing
  const netWeightedAnnual = totalYears > 0
    ? (p1Net * p1Years + p2Net * p2Years + p3Net * p3Years) / totalYears
    : p1Net;

  // DB pension offset (separate from state pension)
  const dbPensionOffset = data.hasDbPension && (data.dbPensionStartAge || 67) <= freedomAge
    ? (data.dbPensionAnnualIncome || 0)
    : 0;

  // State pension offset for display purposes
  const statePensionOffset = spAge <= phase3End ? spAnnual : 0;

  const annualFromPot = Math.max(0, netWeightedAnnual - dbPensionOffset);
  const freedomNumberToday = (annualFromPot / 0.04) + mortgagePV; // 4% rule + mortgage PV
  const freedomNumberInflated = freedomNumberToday * Math.pow(1.025, yearsToFreedom); // 2.5% inflation

  return {
    p1Annual, p2Annual, p3Annual,
    p1Years, p2Years, p3Years,
    weightedAnnual,
    netWeightedAnnual,
    mortgageYearsPastFreedom,
    mortgagePV,
    statePensionOffset,
    statePensionAge: spAge,
    statePensionAnnual: spAnnual,
    dbPensionOffset,
    annualFromPot,
    freedomNumberToday,
    freedomNumberInflated,
    yearsToFreedom,
  };
}
